// One person, several tabs, one idle clock.
//
// The idle logout used to be per tab: a superadmin tab counted only its OWN
// mouse and keys, so working forty minutes in the Admin tab meant coming back
// to a superadmin tab that had "expired" — and because it clears the SHARED
// storage, it killed every other superadmin tab with it. The person never
// left the keyboard; only that one tab was unfocused.
//
// The beacon is a single localStorage timestamp any tab of the app refreshes
// while the user is active. A tab about to declare "idle" first asks the
// beacon; if anyone was active recently, it re-arms instead of logging out.
// A browser that is truly walked away from still logs out on time — no tab
// writes, the beacon goes stale, the timer fires for real.

const BEACON_KEY = 'cap_last_activity_at';

// Writing localStorage on every mousemove would thrash the storage event in
// every other tab; once per few seconds is plenty for a 30-minute window.
const WRITE_EVERY_MS = 5_000;

let lastWrite = 0;

export const recordActivity = (now = Date.now()) => {
  if (now - lastWrite < WRITE_EVERY_MS) return;
  lastWrite = now;
  try {
    localStorage.setItem(BEACON_KEY, String(now));
  } catch (_) {
    // Storage full/blocked — idle logout degrades to per-tab, never crashes.
  }
};

export const lastActivityAt = () => {
  const raw = Number(localStorage.getItem(BEACON_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : null;
};

/**
 * Should a tab whose own timer just expired really log out?
 * Only if NO tab (this one included) has been active within the window.
 * With no beacon at all, trust the local timer — degrade, don't hang open.
 */
export const shouldIdleLogout = (timeoutMs, now = Date.now()) => {
  const last = lastActivityAt();
  if (last === null) return true;
  return now - last >= timeoutMs;
};

/** How long until the shared window expires (for re-arming a timer). */
export const remainingIdleMs = (timeoutMs, now = Date.now()) => {
  const last = lastActivityAt();
  if (last === null) return 0;
  return Math.max(0, timeoutMs - (now - last));
};
